import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackNavigation, trackButtonClick } from '../lib/analytics';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'Navbar');
    navigate('/contact');
    setIsOpen(false);
  };

  const handleWhatsApp = () => {
    trackButtonClick('WhatsApp', 'Navbar');
    window.open('https://wa.me/4407624474095', '_blank');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services & Pricing', path: '/services' },
    { name: 'Showcase', path: '/showcase' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <nav className="fixed w-full z-[60] transition-all duration-500 py-3 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-6xl mx-auto transition-all duration-500 ${
          isScrolled
            ? 'bg-black/85 backdrop-blur-xl border border-white/[0.08] rounded-2xl'
            : 'bg-black/40 backdrop-blur-md border border-white/[0.06] rounded-2xl'
        }`}>
          <div className="relative px-4 sm:px-6">
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.15] to-transparent" />
            </div>

            <div className="relative flex items-center justify-between h-14 lg:h-16">
              <div className="flex items-center">
                <Link
                  to="/"
                  className="text-xs sm:text-base lg:text-xl font-semibold text-white hover:text-primary/90 transition-colors duration-300 flex items-center gap-1 sm:gap-2"
                >
                  <span className="text-primary/80">&lt;</span>
                  <span>AI & Web Solutions</span>
                  <span className="text-primary/60">/&gt;</span>
                </Link>
              </div>

              <div className="hidden lg:flex items-center space-x-0.5">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => trackNavigation(link.path, link.name)}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 group ${
                      location.pathname === link.path
                        ? 'text-white'
                        : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    {location.pathname === link.path && (
                      <span className="absolute inset-0 bg-white/[0.06] rounded-lg" />
                    )}
                    <span className="relative">{link.name}</span>
                    <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 h-px bg-primary/60 transition-all duration-300 ${
                      location.pathname === link.path ? 'w-6' : 'w-0 group-hover:w-6'
                    }`} />
                  </Link>
                ))}
              </div>

              <div className="hidden lg:flex items-center space-x-3">
                <button
                  onClick={handleWhatsApp}
                  className="p-2.5 text-gray-500 hover:text-white hover:bg-white/[0.06] rounded-lg transition-all duration-300"
                  aria-label="Contact via WhatsApp"
                >
                  <MessageCircle className="h-5 w-5" />
                </button>
                <button
                  onClick={handleStartProject}
                  className="btn-primary px-5 py-2.5 text-sm"
                >
                  Start Your Project
                </button>
              </div>

              <div className="flex lg:hidden items-center gap-1.5 sm:gap-2">
                <button
                  onClick={handleStartProject}
                  className="btn-primary px-2 py-1 text-[0.6rem] sm:text-[0.65rem] whitespace-nowrap"
                >
                  Start Project
                </button>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/[0.06] transition-all duration-300"
                  aria-label="Toggle menu"
                >
                  {isOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
          onClick={() => setIsOpen(false)}
        />

        <div className={`absolute inset-y-0 right-0 w-full max-w-sm bg-black/95 backdrop-blur-xl border-l border-white/[0.06] transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full pt-20 pb-6 px-6">
            <div className="flex-1 space-y-1">
              {navLinks.map((link, index) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => {
                    trackNavigation(link.path, link.name);
                    setIsOpen(false);
                  }}
                  className={`block px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-white bg-white/[0.06] border-l-2 border-primary/60'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.03]'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <div className="space-y-3 pt-6 border-t border-white/[0.06]">
              <button
                onClick={handleWhatsApp}
                className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-300 bg-white/[0.03] hover:bg-white/[0.06] rounded-xl border border-white/[0.06] transition-all duration-300"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                WhatsApp Us
              </button>
              <button
                onClick={handleStartProject}
                className="w-full btn-primary py-3.5"
              >
                Start Your Project
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
